import { Button } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Loader } from 'components';
import { useSelectedLocation } from 'contexts';
import dayjs from 'dayjs';
import { useNewTransaction } from 'pages/new-transaction';
import fetchWithRetry from 'services/fetch-retry';
import './access.scss';

const gateImage = require('assets/gate-icons/boom-gate-new.png');
const truckImage = require('assets/gate-icons/gate-entry-exit.png');

type TransactionDateCount = {
  date: Date;
  gateInCount: number;
  gateOutCount: number;
};

const GateAccess: FC = () => {
  const navigate = useNavigate();
  const { selectedLocation } = useSelectedLocation();
  const { resetTransaction } = useNewTransaction();
  const [completedCount, setCompletedCount] = useState<number>();
  const [draftCount, setDraftCount] = useState<number>();

  // reset once on mount to clear stale data (back navigation without form reset)
  useEffect(resetTransaction, [resetTransaction]);

  const newTransaction = useCallback(
    (inbound: boolean, bulk: boolean) => {
      navigate(`/gate/new?inbound=${inbound}&bulk=${bulk}`);
    },
    [navigate]
  );

  const getCompletedCount = useCallback(async () => {
    if (!selectedLocation) return;
    const url = new URL(`/api/transactions/counts`, window.location.origin);
    url.searchParams.append('location', `${selectedLocation.id}`);
    url.searchParams.append('msDateFrom', dayjs().subtract(24, 'hour').valueOf().toString());
    url.searchParams.append('groupBy', 'FOREVER');
    const response = await fetchWithRetry<TransactionDateCount[]>(url, { method: 'GET' }, []);
    setCompletedCount(!response.length ? 0 : response[0].gateInCount + response[0].gateOutCount);
  }, [selectedLocation, setCompletedCount]);

  const getDraftCount = useCallback(async () => {
    if (!selectedLocation) return;
    const url = new URL(`/api/transactions/drafts/count`, window.location.origin);
    url.searchParams.append('locationId', `${selectedLocation.id}`);
    setDraftCount(await fetchWithRetry<number>(url, { method: 'GET' }, 0));
  }, [selectedLocation, setDraftCount]);

  useEffect(() => {
    getCompletedCount();
    getDraftCount();
  }, [getCompletedCount, getDraftCount]);

  return (
    <div className="gate-access d-flex flex-column align-items-center">
      <header className="d-flex flex-column align-items-center">
        <h1 className="font-weight-bold">GATE ACCESS MANAGEMENT</h1>
        <img src={gateImage} alt="gate" />
      </header>
      <section className="d-flex flex-column align-items-center">
        <h3 className="font-weight-regular">RECENT TRANSACTIONS</h3>
        <div className="d-flex">
          <Loader loading={draftCount === undefined}>
            <button
              className="btn-outline d-flex align-items-center justify-content-center"
              onClick={() => navigate('/gate/pending')}
            >
              <span className="material-icons icon-large mr-2">history</span>
              <p>PENDING ({draftCount})</p>
            </button>
          </Loader>
          <Loader loading={completedCount === undefined}>
            <button
              className="btn-outline d-flex align-items-center justify-content-center"
              onClick={() => navigate('/gate/complete')}
            >
              <span className="material-icons icon-large mr-2">check</span>
              <p>COMPLETE ({completedCount})</p>
            </button>
          </Loader>
        </div>
      </section>
      <section className="select d-flex flex-column align-items-center">
        <h3 className="font-weight-regular">NEW TRANSACTION</h3>
        <div className="d-flex">
          <Button className="square" onClick={() => newTransaction(true, false)}>
            <div className="image">
              <img src={truckImage} alt="truck-entry" />
            </div>
            <p className="font-weight-medium">GATE IN</p>
          </Button>
          <Button className="square" onClick={() => newTransaction(false, false)}>
            <div className="image reverse">
              <img src={truckImage} alt="truck-exit" />
            </div>
            <p className="font-weight-medium">GATE OUT</p>
          </Button>
        </div>
        <div className="d-flex">
          <Button onClick={() => newTransaction(true, true)}>
            <p className="font-weight-medium">GATE IN BULK</p>
          </Button>
          <Button onClick={() => newTransaction(false, true)}>
            <p className="font-weight-medium">GATE OUT BULK</p>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default GateAccess;
